import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { CartolaService } from '../../services/cartola.service';
import { HoursOperationService } from '../../services/hours-operation.service';
import { WalletVesClientService } from '../../services/wallet-ves-client.service';
import {
  CupoRegistroMe,
  CupoRegistroService,
} from '../../services/cupo-registro.service';
import {
  AjusteTasaHonrada,
  BeneficiarioVM,
  CambioDeTasa,
  ComprobanteEnvio,
  CotizacionVM,
  DestinoMeta,
  DestinoRemesa,
  EnvioError,
  EnvioOk,
  esCambioDeTasa,
  esEnvioOk,
  OrigenRemesa,
  PerfilUsuario,
  ResultadoEnvio,
  RielColombia,
  TipoLibreta,
} from './models/remittance.model';
import * as Comprobante from './rules/remittance-receipt';
import * as R from './rules/remittance-rules';
import { esTitularVesChileno, tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';
import {
  ContextoBeneficiario,
  RemittanceBeneficiaryService,
} from './services/remittance-beneficiary.service';
import { RemittanceQuoteService } from './services/remittance-quote.service';
import {
  IntencionEnvio,
  RemittanceSubmitService,
} from './services/remittance-submit.service';

/** Pasos del flujo. Los tres últimos se construyen en las fases siguientes. */
type PasoHub = 'cotizacion' | 'beneficiario' | 'resumen' | 'listo';

/**
 * COTIZADOR UNIFICADO — shell del flujo de remesas.
 *
 * Una sola pantalla para elegir con qué se paga, a qué país y cuánto, con la
 * tasa y el monto a recibir siempre visibles. Reemplaza al modal de selección
 * de tipo de remesa como punto de entrada.
 *
 * Este componente NO contiene reglas de negocio: los límites, la matriz de
 * destinos y la comisión viven en `rules/remittance-rules.ts`, y la cotización
 * en `RemittanceQuoteService`. Acá sólo hay estado de UI y orquestación.
 *
 * NO toca backend y NO modifica las pantallas vigentes: convive con ellas.
 */
@Component({
  selector: 'app-remittance-hub',
  templateUrl: './remittance-hub.component.html',
  styleUrls: ['./remittance-hub.component.sass'],
})
export class RemittanceHubComponent implements OnInit, OnDestroy {
  private sub = new Subscription();
  private monto$ = new Subject<number>();
  private recibe$ = new Subject<number>();

  paso: PasoHub = 'cotizacion';

  user: any = null;
  perfil: PerfilUsuario = 'CL';
  /**
   * Perfil CL con saldo VES (familia CL, y VE no `'VE'` como CCVE/V o un tipo
   * vacío con cédula V/E): suma «Bolívares» como moneda de pago, solo hacia
   * Chile (VES→CLP, `retiroVesClp`). El perfil sigue saliendo de
   * `perfilDeTipoId` (VE exacto): el back gatea las remesas VES→COP/PEN y el
   * USDT→CLP del venezolano con `tipoId === 'VE'`, así que CCVE/V operan como
   * el chileno.
   */
  conSaldoVes = false;

  origen: OrigenRemesa = 'CLP';
  destino: DestinoRemesa | '' = '';
  monto: number | null = null;
  rielCo: RielColombia = 'COBREB';

  /**
   * Plazo del riel bancario, en el BOTÓN del riel. Sale de la misma constante
   * que el resumen (`R.ETA_CO_BANCARIO`) para que no vuelvan a decir cosas
   * distintas: antes el botón tenía el texto fijo «Según el banco destino» y el
   * resumen imprimía el `businessDays: 0` del backend como «el mismo día».
   */
  readonly etaBancarioCo = R.ETA_CO_BANCARIO;

  /**
   * Monto en moneda de DESTINO. Es un campo editable, no un espejo: se puede
   * cotizar por lo que paga el cliente o por lo que recibe el beneficiario,
   * como en las pantallas vigentes (`FiatCalculatorComponent:415`).
   */
  montoDestino: number | null = null;

  /** Cuál de los dos campos manda. El otro se espeja con lo que dice el backend. */
  direccion: R.DireccionCalculo = 'ORIGEN';

  /** El campo de destino tiene el foco: no se le corrige el valor al usuario. */
  private destinoEnFoco = false;

  /**
   * Hay un objetivo tipeado esperando su cotización. Mientras esté pendiente el
   * campo no se ajusta: si el usuario tipea y sale del campo antes de que corra
   * el debounce, ajustarlo lo devolvería al valor anterior y se cotizaría ése.
   */
  private objetivoPendiente = false;

  /** Monto inicial del par, derivado del mínimo real. Base de los atajos. */
  private semilla: number | null = null;

  cotizacion: CotizacionVM | null = null;
  cotizando = false;
  desgloseAbierto = false;

  /**
   * Número del último pedido de cotización. Sólo el último escribe en pantalla:
   * dos cotizaciones solapadas (cambio de destino + debounce del monto) se
   * pisaban y ganaba la que respondía última, que podía ser de otro monto o de
   * otro destino.
   */
  private cotizacionSeq = 0;

  saldoClp: number | null = null;
  saldoVes: number | null = null;
  saldoUsdt: number | null = null;

  /**
   * Horario del riel propio (`FIAT` / `FIAT_STOP`). A diferencia del flujo
   * vigente —que expulsa de la pantalla con un Swal— acá es un estado suave:
   * Venezuela y Chile quedan deshabilitados y Colombia y Perú siguen abiertos.
   */
  fiatAbierto = true;

  /**
   * Países Vita activos según el backend. `null` = todavía no cargó.
   * Si el backend apaga Vita o desactiva un país, deja de aparecer en la
   * grilla — la matriz local es el universo posible, no la oferta real.
   */
  destinosVita: Set<string> | null = null;

  /** Par que pidió quien nos enlazó. Ver `tomarPreseleccion`. */
  private preseleccion: { origen: string | null; destino: string | null } | null =
    null;
  private preseleccionAplicada = false;

  /**
   * La consulta de disponibilidad falló. Se distingue del conjunto vacío
   * legítimo: si el backend apagó Vita no hay nada que reintentar, pero si la
   * consulta se cayó la grilla quedaría a medias para siempre.
   */
  destinosVitaFallo = false;

  // ── Paso 2: beneficiario ──
  beneficiarios: BeneficiarioVM[] = [];
  beneficiariosCargando = false;
  beneficiariosError: string | null = null;

  /**
   * Aviso del alta: el beneficiario SÍ se guardó, pero no se pudo usar para
   * seguir. No es un error —no se perdió nada— así que no va en rojo.
   */
  avisoBeneficiario: string | null = null;

  /**
   * Alta en curso. Cubre TODO el ciclo —pedir el schema, el diálogo abierto,
   * recargar la libreta y avanzar—, no sólo el diálogo.
   *
   * El backdrop del diálogo no alcanza: con Vita hay que pedir el schema antes
   * de abrirlo (`getRules`), y al cerrarlo queda otra ventana mientras se
   * recarga la libreta. En esos dos huecos el botón seguía respondiendo y cada
   * click abría otro formulario.
   */
  abriendoAlta = false;
  seleccionado: BeneficiarioVM | null = null;
  busqueda = '';

  // ── Pasos 3 y 4: envío ──
  enviando = false;

  /** Navegación en curso: el segundo click no vuelve a navegar. */
  navegando = false;
  resultado: ResultadoEnvio | null = null;
  avisoCambioTasa: CambioDeTasa | null = null;

  /**
   * Se está enviando con la tasa que el usuario vio, no con la vigente, porque
   * el movimiento favorecía a Aguacate. No se muestra: sólo se reporta.
   */
  private ajusteTasa: AjusteTasaHonrada | null = null;

  /**
   * Comprobante del envío confirmado. Se congela al recibir el OK del backend
   * —no se deriva de la pantalla— así que lo que se comparte es lo que se
   * envió, aunque el cotizador ya haya cambiado de estado.
   */
  comprobante: ComprobanteEnvio | null = null;

  /** Copia en curso: el botón no se puede repetir mientras se resuelve. */
  copiando = false;

  /** Resultado del último intento de copiar, para mostrarlo bajo el botón. */
  comprobanteMsg: string | null = null;

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private quote: RemittanceQuoteService,
    private beneficiaryService: RemittanceBeneficiaryService,
    private submit: RemittanceSubmitService,
    private cartola: CartolaService,
    private walletVes: WalletVesClientService,
    private hoursOperation: HoursOperationService,
    private cupoRegistro: CupoRegistroService,
  ) {}

  ngOnInit(): void {
    this.tomarPreseleccion();

    this.sub.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        if (!user) return;
        const perfilNuevo = R.perfilDeTipoId(user?.tipoId);
        const conSaldoVesNuevo = perfilNuevo === 'CL' && tieneSaldoVes(user?.tipoId, user?.numeroId);
        const primeraCarga = this.user === null;
        this.user = user;

        // El store re-emite con cualquier acción de `auth`. La moneda de pago
        // se ajusta SÓLO en la primera carga o si cambió el perfil: si no, se
        // le pisaba al usuario la que había elegido —quien pagaba con USDT
        // volvía a CLP— dejando la cotización de un origen ya no seleccionado.
        if (primeraCarga || perfilNuevo !== this.perfil || conSaldoVesNuevo !== this.conSaldoVes) {
          this.perfil = perfilNuevo;
          this.conSaldoVes = conSaldoVesNuevo;
          this.origen = this.origenes[0];
        }
        this.cargarSaldos();
        this.aplicarPreseleccion();
      }),
    );

    // Sin `distinctUntilChanged`: los atajos y el cambio de destino escriben el
    // monto por fuera del stream, así que su último valor quedaba desfasado del
    // input y descartaba un tipeo legítimo, dejando en pantalla la cotización
    // anterior. El número de secuencia de `cotizar()` ya evita que dos
    // respuestas se pisen, así que un pedido repetido no hace daño.
    this.sub.add(
      this.monto$.pipe(debounceTime(400)).subscribe(() => this.cotizar()),
    );

    this.sub.add(
      this.recibe$.pipe(debounceTime(400)).subscribe(() => this.cotizar()),
    );

    this.verificarHorarioFiat();
    this.cargarDisponibilidadVita();
    // Límites CLP→VES del servidor (franjas dinámicas): una consulta al entrar,
    // cacheada y compartida con la cotización. Nunca lanza; sin ella mandan
    // las constantes.
    void this.quote.limitesClpServidor('CLP-VES');
  }

  /**
   * Consulta qué rieles Vita están realmente activos. Si el destino elegido
   * deja de estar disponible, se deselecciona: no puede quedar una cotización
   * de un país que el backend apagó.
   */
  async cargarDisponibilidadVita(): Promise<void> {
    this.destinosVitaFallo = false;
    const r = await this.quote.destinosVitaDisponibles();
    this.destinosVita = r.activos;
    this.destinosVitaFallo = r.fallo;
    if (this.destino && !this.destinos.some((d) => d.code === this.destino)) {
      this.destino = '';
      this.limpiar();
    }
    this.aplicarPreseleccion();
  }

  /**
   * Par preseleccionado por quien nos enlazó — hoy, la tarjeta de tasas del
   * home. Se lee del snapshot al entrar: es una intención de arranque, no un
   * estado que la pantalla tenga que seguir.
   *
   * Preselecciona el PAR y nada más. No precarga monto ni salta pasos: los
   * rieles propios no tienen deduplicación de envíos y ningún enlace externo
   * puede acercar al cliente a un POST sin que él lo pida.
   */
  private tomarPreseleccion(): void {
    const q = this.route.snapshot.queryParamMap;
    const origen = q.get('origen');
    const destino = q.get('destino');
    if (origen || destino) this.preseleccion = { origen, destino };
  }

  /**
   * Se aplica una sola vez, y recién cuando están las dos piezas que la
   * validan: el perfil (dice qué orígenes existen) y la disponibilidad de
   * rieles (dice qué destinos se pueden ofrecer). Un par que no pase esos
   * filtros se ignora en silencio — la pantalla abre igual, sin preselección.
   */
  private aplicarPreseleccion(): void {
    if (!this.preseleccion || this.preseleccionAplicada) return;
    if (!this.user || this.destinosVita === null) return;

    this.preseleccionAplicada = true;
    const { origen, destino } = this.preseleccion;

    if (origen && this.origenes.indexOf(origen as OrigenRemesa) >= 0) {
      this.origen = origen as OrigenRemesa;
    }
    if (destino && this.destinos.some((d) => d.code === destino)) {
      this.selectDestino(destino as DestinoRemesa);
    }
  }

  /** Reintento manual: la grilla no puede quedar a medias por un fallo de red. */
  reintentarDestinos(): void {
    this.destinosVita = null;
    this.cargarDisponibilidadVita();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // ───────────────────── Opciones (delegadas a las reglas) ─────────────────────

  get origenes(): OrigenRemesa[] {
    return R.origenesDe(this.perfil, this.conSaldoVes);
  }

  /**
   * Destinos que se muestran. Parten de la matriz (perfil × origen) pero se
   * FILTRAN por lo que el backend tiene realmente activo.
   *
   * Los rieles propios (Venezuela y Chile) no dependen de Vita: se gatean por
   * horario. Los rieles Vita (Colombia y Perú) sólo aparecen si el flag global
   * está encendido y el país viene en el catálogo de activos.
   *
   * Mientras la disponibilidad no cargó (`null`) se ocultan: es preferible que
   * aparezcan un instante después a mostrarlos y tener que sacarlos.
   */
  get destinos(): DestinoMeta[] {
    return R.destinosDe(this.perfil, this.origen).filter((d) => {
      if (R.esRielPropio(d.code)) return true;
      return this.destinosVita?.has(d.code) === true;
    });
  }

  /** `null` mientras se consulta la disponibilidad de los rieles Vita. */
  get cargandoDestinos(): boolean {
    return this.destinosVita === null;
  }

  /** No hay ningún destino disponible para el origen elegido. */
  get sinDestinos(): boolean {
    return !this.cargandoDestinos && this.destinos.length === 0;
  }

  get destinoMeta(): DestinoMeta | null {
    return this.destino ? R.DESTINOS[this.destino] : null;
  }

  /**
   * Atajos derivados de la semilla del par —que a su vez sale del mínimo real
   * que devolvió el backend—, no de constantes: el primer atajo era exactamente
   * el mínimo, y elegirlo dejaba la pantalla en el borde donde se bloquea.
   */
  get chips(): number[] {
    if (!this.destino) return [];
    return R.chipsDesdeSemilla(
      this.origen,
      this.semilla,
      this.cotizacion?.max ?? null,
    );
  }

  /** El destino está cerrado por horario (sólo aplica al riel propio). */
  esDestinoCerrado(code: DestinoRemesa): boolean {
    return R.tieneHorarioFiat(code) && !this.fiatAbierto;
  }

  get saldoActual(): number | null {
    return this.saldoDe(this.origen);
  }

  /** Saldo de cualquier moneda de pago. `null` = todavía no cargó o falló. */
  saldoDe(o: OrigenRemesa): number | null {
    if (o === 'CLP') return this.saldoClp;
    if (o === 'VES') return this.saldoVes;
    return this.saldoUsdt;
  }

  /**
   * Decimales TIPEABLES en el campo "Tú envías" (`appMonto`). No es la máscara
   * de visualización: es qué puede escribir el usuario. USDT admite centavos;
   * las fiat que operamos —CLP y VES— se envían enteras.
   */
  get decimalesOrigen(): number {
    return this.origen === 'USDT' ? 2 : 0;
  }

  /**
   * Ídem para "El beneficiario recibe". Mismo criterio que la máscara del
   * desglose (`R.decimalesDe`): sólo PEN lleva centavos.
   */
  get decimalesDestino(): number {
    return this.destino === 'PE' ? 2 : 0;
  }

  /** Máscara de decimales del saldo: USDT lleva 2, las fiat son enteras. */
  decimalesSaldo(o: OrigenRemesa): string {
    return o === 'USDT' ? '1.2-2' : '1.0-0';
  }

  /**
   * El saldo no alcanza para el monto tipeado. Aplica SÓLO a la moneda con la
   * que se está pagando: el monto está en moneda de origen, así que compararlo
   * contra el saldo de otra moneda no significa nada —marcaba "No alcanza" en
   * USDT con 9.500 USDT de saldo porque el monto eran 15.000 VES— y convertirlo
   * exigiría una cotización por cada moneda del selector.
   */
  saldoNoAlcanza(o: OrigenRemesa): boolean {
    if (o !== this.origen) return false;
    const s = this.saldoDe(o);
    return s != null && this.monto != null && Number(this.monto) > s;
  }

  /** Etiqueta legible de la moneda de pago. */
  labelOrigen(o: OrigenRemesa): string {
    if (o === 'CLP') return 'Pesos CLP';
    if (o === 'VES') return 'Bolívares';
    return 'USDT';
  }

  // ───────────────────── Interacción ─────────────────────

  selectOrigen(o: OrigenRemesa): void {
    if (this.origen === o) return;
    this.origen = o;
    // El destino no se auto-selecciona: la matriz cambió bajo los pies.
    this.destino = '';
    this.limpiar();
    this.cargarSaldos();
  }

  /**
   * Elegir destino cotiza UNA sola vez, y sólo excepcionalmente dos.
   *
   * El mínimo de nueve de los diez pares ya se conoce sin preguntar —son los
   * mismos que usan las pantallas vigentes, `limitesLocales()`—, así que el
   * monto inicial se elige de entrada y la primera cotización que sale es ya
   * la del monto que el usuario ve. El único sin mínimo conocido es VES→CL,
   * que arranca con `MONTO_INICIAL_VES_CL`.
   *
   * Si esa cotización vuelve con `bajoMinimo` —el mínimo real estaba por encima
   * del inicial— se corrige el monto con el mínimo que vino en ella y se cotiza
   * de nuevo. Preguntar primero costaba un viaje de red en TODOS los casos para
   * cubrir el que casi nunca pasa.
   */
  async selectDestino(code: DestinoRemesa): Promise<void> {
    if (this.esDestinoCerrado(code)) return;
    // Un click es uno: mientras se cotiza el mismo destino, el segundo no
    // arranca otra cadena. Cambiar a otro país sí interrumpe, como debe ser.
    if (this.destino === code && this.cotizando) return;
    this.destino = code;
    this.direccion = 'ORIGEN';
    this.limpiar();

    // CLP→VE: si ya se conocen los límites que publica el servidor (franjas
    // dinámicas) se usan para la semilla; si no, las constantes de siempre. Es
    // síncrono a propósito: la consulta la hace (cacheada) la cotización, y si
    // el mínimo real no coincide, la corrección de abajo lo ajusta.
    const clpServidor =
      this.origen === 'CLP' && code === 'VE'
        ? this.quote.limitesClpConocidos('CLP-VES')
        : null;
    const locales = R.limitesLocales(this.origen, code, clpServidor);
    this.semilla = this.elegirSemilla(locales?.min ?? null, locales?.max ?? null);
    this.monto = this.semilla;
    await this.cotizar();

    // Corrección: sólo si el backend dice que el monto inicial no llega.
    const c = this.cotizacion;
    const intacto =
      this.destino === code &&
      this.direccion === 'ORIGEN' &&
      this.monto === this.semilla;
    if (!intacto || !c?.bajoMinimo || c.min == null) return;

    const corregida = this.elegirSemilla(c.min, c.max);
    if (corregida === this.monto) return;

    this.semilla = corregida;
    this.monto = corregida;
    await this.cotizar();
  }

  /** Monto inicial del par. Sin mínimo conocido, el típico de VES→CL. */
  private elegirSemilla(min: number | null, max: number | null): number {
    return (
      R.semillaDesdeMinimo(this.origen, min, max, this.saldoActual) ??
      R.MONTO_INICIAL_VES_CL
    );
  }

  /**
   * Cambiar el riel de Colombia cambia la VARIANTE de Vita, y con ella qué
   * beneficiarios son válidos: uno cargado para Bre-B no sirve para
   * transferencia bancaria. Por eso se descarta la selección previa.
   */
  selectRielCo(riel: RielColombia): void {
    if (this.rielCo === riel) return;
    this.rielCo = riel;
    this.seleccionado = null;
    this.beneficiarios = [];
    this.cotizar();
  }

  onMontoChange(): void {
    this.direccion = 'ORIGEN';
    this.monto$.next(Number(this.monto) || 0);
  }

  /** El usuario tipea cuánto quiere que RECIBA el beneficiario. */
  onMontoDestinoChange(): void {
    this.direccion = 'DESTINO';
    this.objetivoPendiente = true;
    this.recibe$.next(Number(this.montoDestino) || 0);
  }

  onFocoDestino(): void {
    this.destinoEnFoco = true;
    this.direccion = 'DESTINO';
  }

  onBlurDestino(): void {
    this.destinoEnFoco = false;
    // Con un tipeo en camino no se toca: se ajusta cuando llegue la cotización.
    if (!this.objetivoPendiente && !this.cotizando) this.ajustarMontoDestino();
  }

  /**
   * Lo más que se puede enviar ahora: el saldo de la moneda de pago (en CLP ya
   * trae el sobregiro), acotado por el máximo del destino y por el cupo de
   * registro si rige. Truncado, nunca redondeado: redondear hacia arriba
   * dejaría el monto un centavo por encima del saldo. `null` = sin saldo.
   */
  get montoMaximo(): number | null {
    const saldo = this.saldoActual;
    if (!this.destino || saldo == null || saldo <= 0) return null;
    const topes = [saldo, this.cotizacion?.max, this.cupoDisponible].filter(
      (v): v is number => v != null,
    );
    const factor = 10 ** this.decimalesOrigen;
    // El 1e-9 absorbe el error de coma flotante: 12.34 * 100 = 1233.999…
    const max = Math.trunc(Math.min(...topes) * factor + 1e-9) / factor;
    return max > 0 ? max : null;
  }

  usarMaximo(): void {
    const max = this.montoMaximo;
    if (max != null) this.aplicarChip(max);
  }

  aplicarChip(valor: number): void {
    this.direccion = 'ORIGEN';
    this.monto = valor;
    this.cotizar();
  }

  private limpiar(): void {
    this.cotizacion = null;
    this.montoDestino = null;
    this.semilla = null;
    this.desgloseAbierto = false;
  }

  // ───────────────────── Cotización ─────────────────────

  /**
   * ÚNICO camino a la cotización, en las dos direcciones.
   *
   * Cada pedido lleva número de secuencia y sólo el último escribe en pantalla.
   * Sin eso, dos cotizaciones solapadas —el cambio de destino y el debounce del
   * monto— se pisaban: ganaba la que respondía última y `cotizando` lo apagaba
   * la primera, así que el skeleton se iba mostrando un número que no
   * correspondía al monto en el input.
   */
  async cotizar(): Promise<void> {
    const mio = ++this.cotizacionSeq;
    const dir = this.direccion;

    const monto = Number(this.monto);
    const objetivo = Number(this.montoDestino);
    const hayEntrada = dir === 'DESTINO' ? objetivo > 0 : monto > 0;

    if (!this.destino || !hayEntrada) {
      this.cotizacion = null;
      this.cotizando = false;
      this.objetivoPendiente = false;
      return;
    }

    this.cotizando = true;
    try {
      const c =
        dir === 'DESTINO'
          ? await this.quote.cotizarPorDestino(
              this.origen,
              this.destino,
              objetivo,
              this.rielCo,
              this.cotizacion,
            )
          : await this.quote.cotizar(this.origen, this.destino, monto, this.rielCo);

      if (mio !== this.cotizacionSeq) return; // llegó tarde: se descarta
      if (dir === 'DESTINO') this.objetivoPendiente = false;
      this.aplicarCotizacion(c, dir);
    } finally {
      if (mio === this.cotizacionSeq) this.cotizando = false;
    }
  }

  /**
   * Espeja el campo que el usuario NO está editando con lo que dijo el backend.
   * El `recibe` que se muestra es siempre el de la cotización: el front ajusta
   * el monto de origen, nunca el de destino.
   */
  private aplicarCotizacion(c: CotizacionVM, dir: R.DireccionCalculo): void {
    this.cotizacion = c;

    if (dir === 'ORIGEN') {
      // Si el usuario ya está escribiendo en el campo de destino, no se le
      // pisa lo tipeado con el espejo de una cotización anterior.
      if (this.destinoEnFoco) return;
      this.montoDestino =
        c.recibe != null ? R.redondearDestino(c.destino, c.recibe) : null;
      return;
    }

    this.monto = c.monto;
    // Si el usuario ya salió del campo, se corrige al recibe real.
    if (!this.destinoEnFoco) this.ajustarMontoDestino();
  }

  /**
   * Ajusta el campo de destino al recibe REAL de la cotización.
   *
   * El monto de origen tiene que ser pagable —entero en las fiat—, así que el
   * recibe casi nunca cae exacto en lo que se tipeó. Dejar el número del
   * usuario cuando el backend va a pagar otro sería mentirle. Mismo criterio
   * que el redondeo al salir del campo de las pantallas vigentes
   * (`CalculatorCryptoComponent.onUsdtBlur:94`).
   */
  private ajustarMontoDestino(): void {
    const c = this.cotizacion;
    if (c?.recibe == null) return;
    this.montoDestino = R.redondearDestino(c.destino, c.recibe);
  }

  /**
   * Lo que da la tasa mostrada antes de la comisión, en moneda de destino.
   * Es la fila que hace visible la cuenta: `subtotal − comisión = recibe`.
   */
  get subtotal(): number | null {
    const c = this.cotizacion;
    if (!c) return null;
    return R.subtotalEnDestino(c.monto, R.tasaMostrada(c), c.origen, c.destino);
  }

  // ───────────────────── Estado derivado ─────────────────────

  /** Hay comisión real que mostrar (sólo rieles COP y PEN). */
  get hayComision(): boolean {
    const c = this.cotizacion;
    return !!c && !c.sinComision && c.comision != null;
  }

  /** El riel publica tasa de referencia. Hoy sólo COP y PEN. */
  get hayReferencia(): boolean {
    return this.cotizacion?.tasaReferencia != null;
  }

  /**
   * ÚNICA tasa que se le muestra al cliente.
   *
   * Cuando el riel publica referencia se muestra ÉSA y sólo ésa. Mostrar
   * además la aplicada pone dos tasas distintas en la misma pantalla —
   * 3,5795 y 3,3847 para el mismo envío— y el cliente termina restando el
   * spread. La diferencia entre las dos ya está informada como comisión, en
   * moneda de origen, y la identidad cierra:
   * `(monto − comisión) × referencia = recibe`.
   *
   * Mismo criterio que las pantallas vigentes, que ocultan la tasa del medio
   * cuando hay referencia (`vita-calculator-demo.component.html:673-675`).
   */
  get tasaVisible(): number | null {
    return R.tasaMostrada(this.cotizacion);
  }

  /** Etiqueta de `tasaVisible`: nombra cuál de las dos se está mostrando. */
  get etiquetaTasa(): string {
    return R.etiquetaTasaMostrada(this.cotizacion);
  }

  /** Formato de la tasa del par vigente. */
  get fmtTasa(): string {
    const c = this.cotizacion;
    return c ? R.formatoTasa(c.origen, c.destino) : '1.2-6';
  }

  /** Formato de la comisión del par vigente. Va en moneda de destino. */
  get fmtComision(): string {
    const d = this.cotizacion?.destino ?? this.destino;
    return d ? R.formatoComision(d) : '1.0-0';
  }

  get puedeDesplegarDesglose(): boolean {
    const c = this.cotizacion;
    if (!c || this.cotizando) return false;
    return c.disponible && c.recibe != null && c.tasaEfectiva != null;
  }

  get saldoInsuficiente(): boolean {
    return (
      this.saldoActual != null &&
      this.monto != null &&
      Number(this.monto) > this.saldoActual
    );
  }

  /**
   * Texto del bloqueo por saldo. Se guarda aparte porque es el ÚNICO que el
   * usuario puede destrabar solo, y la vista necesita distinguirlo del resto
   * para ofrecerle la recarga en vez de un aviso muerto.
   */
  private readonly MSG_SALDO = 'Tu saldo no alcanza para este envío.';

  // ── Cupo de registro (empresa pre-aprobada) ──────────────────────
  // El backend le pone un tope en CLP a las empresas PRE-aprobadas
  // (`cupo_registro`). El `disponible` restante lo calcula el backend, NO el
  // front. Sólo aplica cuando se paga en CLP y el tope está vigente
  // (`aplicaTope`). Si el monto lo supera, se bloquea ANTES de enviar: el
  // `POST transaccion` responde 200 y valida el cupo async, así que sin este
  // corte el cliente vería "transacción creada" y el backend la descartaría.

  get cupoMe(): CupoRegistroMe | null {
    return this.cupoRegistro.me;
  }

  /** `true` si el tope de registro rige para el envío actual (pago en CLP). */
  get cupoAplica(): boolean {
    const me = this.cupoMe;
    return (
      this.origen === 'CLP' &&
      !!me &&
      me.aplicaTope === true &&
      me.disponible != null
    );
  }

  /** CLP que la empresa pre-aprobada puede enviar sin superar su cupo. */
  get cupoDisponible(): number | null {
    return this.cupoAplica ? this.cupoMe?.disponible ?? null : null;
  }

  get cupoExcedido(): boolean {
    return (
      this.cupoAplica &&
      this.monto != null &&
      this.cupoDisponible != null &&
      Number(this.monto) > this.cupoDisponible
    );
  }

  get cupoMensaje(): string {
    return `Tu registro está pre-aprobado: puedes enviar hasta ${this.fmt(
      this.cupoDisponible ?? 0,
    )} CLP. Completa el registro de tu empresa para operar sin este límite.`;
  }

  /** Primer impedimento que aplique, en orden de prioridad. `null` = se puede seguir. */
  get bloqueo(): string | null {
    const c = this.cotizacion;
    if (!c) return null;
    if (!c.disponible) return c.motivo || 'Este destino no está disponible ahora.';
    if (c.bajoMinimo && c.min != null) {
      return `El mínimo para este destino es ${this.fmt(c.min)} ${c.monedaOrigen}.`;
    }
    if (c.excedeMaximo && c.max != null) {
      return `El máximo por envío es ${this.fmt(c.max)} ${c.monedaOrigen}.`;
    }
    if (this.cupoExcedido) return this.cupoMensaje;
    if (!c.activo) return 'Este destino no acepta envíos en este momento.';
    if (this.saldoInsuficiente) return this.MSG_SALDO;
    return null;
  }

  /**
   * Falta plata en la moneda de pago. Dos casos, los dos se resuelven igual:
   *
   *  1. el monto tipeado supera el saldo;
   *  2. el saldo no llega ni al MÍNIMO del destino, así que no hay monto
   *     válido que el usuario pueda pagar.
   *
   * El segundo caso es el de los rieles Vita, cuyo mínimo (20 USD) es muy
   * superior al de Venezuela y Chile: con poco saldo el usuario tipea montos
   * bajos, ganaba `bajoMinimo` en la prioridad de `bloqueo` y el aviso de saldo
   * no aparecía nunca. Por eso esto NO compite en prioridad con los demás
   * impedimentos: se muestra en paralelo, para que "el mínimo es 20.000" venga
   * acompañado de "y tampoco tenés con qué pagarlo".
   */
  get faltaSaldo(): boolean {
    const c = this.cotizacion;
    const saldo = this.saldoActual;
    if (!c || saldo == null) return false;
    if (this.saldoInsuficiente) return true;
    return c.min != null && saldo < c.min;
  }

  /**
   * El impedimento que NO es el saldo. Ése tiene su propio aviso accionable,
   * así que acá se omite para no decir dos veces lo mismo en dos colores.
   */
  get otroBloqueo(): string | null {
    const b = this.bloqueo;
    if (b === this.MSG_SALDO) return null;
    // El bloqueo por cupo tiene su propio aviso, con el CTA para completar el
    // registro. Se omite acá para no repetir el mensaje en dos lugares.
    if (this.cupoExcedido && b === this.cupoMensaje) return null;
    return b;
  }

  /** Reanuda el registro de la empresa (el monolito sabe retomarlo). */
  irACompletarRegistro(): void {
    if (this.navegando) return;
    this.navegando = true;
    this.router.navigate(['/wallet/client/company-registration']);
  }

  /**
   * Recarga de la moneda con la que se paga.
   *
   * Mismo criterio que el home del cliente (`user-home`): las fiat —CLP y
   * VES— se recargan en `rechargeAccount`, que ya bifurca sus cuentas por
   * `tipoId`, y USDT en `load-pays-usdt`. No se manda a un usuario VE a la
   * pantalla de USDT cuando lo que le falta son bolívares, que es justo lo que
   * hace hoy `FiatCalculatorComponent:1319`.
   *
   * 30/10/2026: el chileno no recarga bolívares. Pagando con VES no tiene el
   * botón (`ofreceRecarga`) y ya no se manda `?moneda=VES`, que le abría la
   * recarga VES: `rechargeAccount` solo se la muestra al VE.
   */
  irARecargar(): void {
    if (this.navegando || !this.ofreceRecarga) return;
    this.navegando = true;
    if (this.origen === 'USDT') {
      this.router.navigate(['/wallet/client/load-pays-usdt']);
      return;
    }
    this.router.navigate(['/wallet/client/rechargeAccount']);
  }

  /** El aviso de falta de saldo lleva «Recargar»: no con VES del chileno. */
  get ofreceRecarga(): boolean {
    return R.ofreceRecarga(this.perfil, this.origen);
  }

  /**
   * Chileno enviando a Venezuela: puede, en vez de enviar, guardar los
   * bolívares en su propio saldo VES.
   *  · CLP → `comprar-bolivares` (el back la permite solo a la familia CL).
   *  · USDT → `convert-usdt-ves`, opción «Guardar en Wallet VES» (cualquiera
   *    con saldo VES).
   */
  get ofreceGuardarEnSaldoVes(): boolean {
    if (this.perfil !== 'CL' || this.destino !== 'VE') return false;
    if (this.origen === 'CLP') return esTitularVesChileno(this.user?.tipoId);
    if (this.origen === 'USDT') return this.conSaldoVes;
    return false;
  }

  irAGuardarEnSaldoVes(): void {
    if (this.navegando) return;
    this.navegando = true;
    const ruta =
      this.origen === 'USDT'
        ? '/wallet/client/convert-usdt-ves'
        : '/wallet/client/comprar-bolivares';
    this.router.navigate([ruta]);
  }

  /**
   * VES→CLP del chileno (o CCVE/V): los bolívares salen a SU cuenta de retiro
   * en Chile (`retiroVesClp` exige una cuenta de retiro del propio usuario),
   * no a un tercero. Cambia los textos de beneficiario, resumen y resultado.
   */
  get aCuentaPropia(): boolean {
    return this.perfil === 'CL' && this.origen === 'VES' && this.destino === 'CL';
  }

  /**
   * Nota del paso 4. Al chileno se le dice dónde queda la plata si el envío
   * se rechaza: en los rieles propios (CLP→VES con `createTRX`, USDT→VES con
   * `SWAP_USDT_VES`) la devolución de un chileno queda en su saldo VES, no en
   * CLP/USDT; en VES→CLP vuelve, como siempre, a su saldo VES.
   */
  get notaResultado(): string {
    if (this.aCuentaPropia) {
      return 'Te avisamos cuando los pesos lleguen a tu cuenta. Si el retiro se rechaza, los bolívares vuelven a tu saldo VES.';
    }
    const c = this.cotizacion;
    if (esTitularVesChileno(this.user?.tipoId) && c?.destino === 'VE' && (c.origen === 'CLP' || c.origen === 'USDT')) {
      return 'Te avisamos cuando el dinero llegue al beneficiario. Si el envío se rechaza, la devolución queda en tu saldo VES (bolívares).';
    }
    return 'Te avisamos cuando el dinero llegue al beneficiario.';
  }

  get puedeContinuar(): boolean {
    return !!this.cotizacion && !this.cotizando && this.bloqueo === null;
  }

  private fmt(n: number): string {
    return Math.round(n).toLocaleString('es-CL');
  }

  /**
   * Avanza al paso de beneficiario. No navega a las pantallas vigentes: el
   * flujo se completa dentro de este componente.
   */
  continuar(): void {
    // Un click es uno: el segundo llegaría antes de que el template desmonte el
    // botón y dispararía otra carga de la libreta.
    if (this.paso !== 'cotizacion' || !this.puedeContinuar) return;
    this.paso = 'beneficiario';
    this.cargarBeneficiarios();
  }

  volverACotizacion(): void {
    this.paso = 'cotizacion';
  }

  // ───────────────────── Paso 2: beneficiario ─────────────────────

  /** Contexto que necesita el servicio para resolver la libreta correcta. */
  private get contextoBeneficiario(): ContextoBeneficiario | null {
    if (!this.destino) return null;
    return {
      destino: this.destino,
      usuarioId: Number(this.user?.usuarioId),
      // Lo que RECIBE el beneficiario: el techo de Pago Móvil se mide contra esto.
      recibe: this.cotizacion?.recibe ?? null,
      rielCo: this.rielCo,
      esUsuarioVe: this.perfil === 'VE',
    };
  }

  get libretaActual(): TipoLibreta | null {
    return this.destino ? this.beneficiaryService.libretaDe(this.destino) : null;
  }

  async cargarBeneficiarios(): Promise<void> {
    const ctx = this.contextoBeneficiario;
    if (!ctx) return;

    this.beneficiariosCargando = true;
    this.beneficiariosError = null;
    this.avisoBeneficiario = null;
    try {
      this.beneficiarios = await this.beneficiaryService.listar(ctx);
      // Si el que estaba elegido ya no está en la lista o quedó bloqueado
      // (cambió el monto, cambió el riel), se descarta.
      this.revalidarSeleccion();
    } catch {
      this.beneficiarios = [];
      this.beneficiariosError =
        'No pudimos cargar tus beneficiarios. Intenta nuevamente.';
    } finally {
      this.beneficiariosCargando = false;
    }
  }

  /** La selección deja de ser válida si desaparece de la lista o se bloquea. */
  private revalidarSeleccion(): void {
    if (!this.seleccionado) return;
    const vigente = this.beneficiarios.find((b) => b.id === this.seleccionado!.id);
    this.seleccionado = vigente && !vigente.bloqueado ? vigente : null;
  }

  /**
   * Beneficiarios visibles sin desplazar. Por encima de esto la lista scrollea
   * dentro de su propio contenedor, como hace hoy el flujo de Vita
   * (`max-height` + `overflow-y`), en vez de crecer sin límite.
   */
  readonly BENEFICIARIOS_VISIBLES = 5;

  /** Hay más de los que entran a la vista: conviene avisar que se puede scrollear. */
  get hayMasBeneficiarios(): boolean {
    return this.beneficiariosFiltrados.length > this.BENEFICIARIOS_VISIBLES;
  }

  get beneficiariosFiltrados(): BeneficiarioVM[] {
    const q = this.busqueda.trim().toLowerCase();
    if (!q) return this.beneficiarios;
    return this.beneficiarios.filter((b) =>
      `${b.titulo} ${b.subtitulo} ${b.badges.join(' ')}`.toLowerCase().includes(q),
    );
  }

  seleccionar(b: BeneficiarioVM): void {
    if (b.bloqueado) return;
    this.seleccionado = b;
    // El aviso del alta hablaba de una selección que ya no aplica.
    this.avisoBeneficiario = null;
  }

  /**
   * Abre el alta, selecciona el recién creado y AVANZA AL RESUMEN.
   *
   * Guardar un beneficiario dentro de un envío es un paso intermedio: el
   * usuario lo carga para usarlo ahora, no para administrar su libreta. Dejarlo
   * de vuelta en la lista lo obliga a buscar el que acaba de crear y apretar
   * "Continuar".
   *
   * Se avanza sólo con identificación POSITIVA del nuevo. Los diálogos de
   * Venezuela y Chile confirman que guardaron pero no dicen qué, y si el recién
   * creado no aparece en la libreta —lo esconde el filtro de Pago Móvil, es de
   * otra variante— avanzar seleccionaría a otra persona a un clic de confirmar
   * un envío. En ese caso se queda en la lista y se lo dice.
   *
   * El resumen no envía nada y tiene "volver": avanzar es reversible.
   */
  async nuevoBeneficiario(): Promise<void> {
    if (this.abriendoAlta) return;
    const ctx = this.contextoBeneficiario;
    if (!ctx) return;

    const previos = new Set(this.beneficiarios.map((b) => b.id));
    this.abriendoAlta = true;

    try {
      const alta = await this.beneficiaryService.abrirAlta(ctx, previos);
      // Cancelar no toca nada: el que estaba elegido sigue elegido.
      if (!alta.guardado) return;

      // Se guardó algo nuevo, así que la selección anterior ya no representa la
      // intención del usuario: la reemplaza el nuevo, o no queda ninguna.
      this.seleccionado = null;
      // El buscador podría estar escondiendo justamente al recién creado.
      this.busqueda = '';
      await this.cargarBeneficiarios();

      if (!alta.beneficiario) {
        this.avisoBeneficiario =
          'Guardamos el beneficiario, pero no aparece en la libreta de este destino. Revisa que sea del tipo correcto y elige a quién le envías.';
        return;
      }

      // Se prefiere la versión de la lista: trae el bloqueo ya evaluado contra
      // el monto de esta cotización.
      const nuevo =
        this.beneficiarios.find((b) => b.id === alta.beneficiario!.id) ??
        alta.beneficiario;

      if (nuevo.bloqueado) {
        this.seleccionado = null;
        this.avisoBeneficiario =
          nuevo.motivoBloqueo ??
          'El beneficiario se guardó, pero no se puede usar para este envío.';
        return;
      }

      this.seleccionado = nuevo;
      this.irAResumen();
    } catch {
      this.beneficiariosError =
        'No pudimos abrir el formulario. Intenta nuevamente.';
    } finally {
      this.abriendoAlta = false;
    }
  }

  get puedeIrAResumen(): boolean {
    return !!this.seleccionado && !this.seleccionado.bloqueado;
  }

  irAResumen(): void {
    if (this.paso !== 'beneficiario' || !this.puedeIrAResumen) return;
    this.resultado = null;
    this.avisoCambioTasa = null;
    this.ajusteTasa = null;
    this.paso = 'resumen';
  }

  volverABeneficiario(): void {
    this.paso = 'beneficiario';
  }

  // ───────────────────── Paso 3: resumen y envío ─────────────────────

  /**
   * Confirma el envío. El orden importa:
   *   1. congelar lo que el usuario vio (`tomarSnapshot`),
   *   2. re-cotizar y comparar (`verificarTasa`) — si la tasa cambió NO se
   *      envía nada y se pide re-confirmar con los valores nuevos,
   *   3. recién ahí, enviar.
   *
   * Es la protección contra desfase de tasa que hoy sólo tienen los rieles de
   * Vita; acá aplica a todos, sin backend nuevo.
   */
  async confirmarEnvio(): Promise<void> {
    if (!this.cotizacion || !this.seleccionado || this.enviando) return;

    this.enviando = true;
    this.resultado = null;
    this.avisoCambioTasa = null;
    this.ajusteTasa = null;

    try {
      // 30/10/2026: el horario del riel propio (Venezuela y Chile) se vuelve a
      // consultar justo antes de enviar. Al entrar ya se miraba, pero un cierre
      // del operador con la pantalla abierta no frenaba el envío. Si la
      // consulta falla, `verificarHorarioFiat` deja el riel abierto.
      if (R.tieneHorarioFiat((this.cotizacion as CotizacionVM).destino)) {
        await this.verificarHorarioFiat();
        if (!this.fiatAbierto) {
          this.resultado = {
            ok: false,
            mensaje: 'Hemos cerrado operaciones, muchas gracias por preferirnos.',
            contactarOperador: false,
            ambiguo: false,
          };
          return;
        }
      }

      // Cupo de registro: se re-valida contra el backend JUSTO antes de
      // despachar (cierra la carrera con un cupo consumido en otra pestaña).
      // El `POST transaccion` responde 200 y valida el cupo async, así que si
      // no cortáramos acá el cliente vería "transacción creada" y el backend la
      // descartaría en silencio. `refreshMe` es fail-closed; si no se puede
      // leer, caemos al último valor conocido para no bloquear un envío legítimo.
      if (this.origen === 'CLP') {
        const prev = this.cupoMe;
        const me = (await this.cupoRegistro.refreshMe()) ?? prev;
        if (
          me?.aplicaTope === true &&
          me.disponible != null &&
          this.monto != null &&
          Number(this.monto) > me.disponible
        ) {
          this.resultado = {
            ok: false,
            mensaje: `Tu registro está pre-aprobado: puedes enviar hasta ${this.fmt(
              me.disponible,
            )} CLP. Completa el registro de tu empresa para operar sin este límite.`,
            contactarOperador: false,
            ambiguo: false,
          };
          return;
        }
      }

      const snap = this.submit.tomarSnapshot(this.cotizacion);
      if (!snap) {
        this.resultado = {
          ok: false,
          mensaje: 'No hay una cotización vigente. Vuelve a cotizar.',
          contactarOperador: false,
          ambiguo: false,
        };
        return;
      }

      const { bloqueo, ajuste } = await this.submit.verificarTasa(
        snap,
        this.rielCo,
      );
      if (bloqueo) {
        if (esCambioDeTasa(bloqueo)) {
          // Se actualiza lo que se muestra y se espera una nueva confirmación.
          this.avisoCambioTasa = bloqueo;
          this.cotizacion = bloqueo.cotizacion;
          this.ajustarMontoDestino();
          this.revalidarSeleccion();
          return;
        }
        this.resultado = bloqueo;
        return;
      }

      // La cotización NO se reemplaza: se envía la que el usuario vio.
      this.ajusteTasa = ajuste;
      await this.ejecutarEnvio(false);
    } finally {
      this.enviando = false;
    }
  }

  /** El usuario aceptó enviar pese al aviso de posible duplicado. */
  async enviarIgual(): Promise<void> {
    if (this.enviando) return;
    this.enviando = true;
    this.resultado = null;
    try {
      await this.ejecutarEnvio(true);
    } finally {
      this.enviando = false;
    }
  }

  private async ejecutarEnvio(confirmarDuplicado: boolean): Promise<void> {
    const r = confirmarDuplicado
      ? await this.submit.confirmarDuplicado(this.intencion())
      : await this.submit.enviar(this.intencion());

    this.resultado = r;
    if (esEnvioOk(r)) {
      this.reportarTasaHonrada(r.referencia);
      this.comprobante = Comprobante.construirComprobante({
        envio: r,
        cotizacion: this.cotizacion as CotizacionVM,
        beneficiario: this.seleccionado as BeneficiarioVM,
        destino: R.DESTINOS[(this.cotizacion as CotizacionVM).destino],
        rielCo: this.rielCo,
        enviadoEn: Date.now(),
      });
      this.paso = 'listo';
    }
    // Un resultado ambiguo también termina el flujo: no se puede reintentar
    // a ciegas, hay que verificar en el historial.
    else if ((r as any).ambiguo) this.paso = 'listo';
    // El servidor rechazó la tasa (`esRechazoPorTasa`): se vuelve a cotizar con el flujo de
    // siempre y el usuario confirma de nuevo con los valores nuevos.
    else if ((r as any).recotizar) {
      try {
        await this.cotizar();
      } catch {
        // Mismo texto que el cotizador cuando no puede calcular.
        this.resultado = {
          ok: false,
          mensaje: 'No pudimos calcular el envío. Intenta de nuevo.',
          contactarOperador: false,
          ambiguo: false,
        };
      }
    }
  }

  private intencion(): IntencionEnvio {
    return {
      usuarioId: Number(this.user?.usuarioId),
      cotizacion: this.cotizacion as CotizacionVM,
      beneficiario: this.seleccionado as BeneficiarioVM,
      rielCo: this.rielCo,
    };
  }

  // ── Estado del resultado, para la vista ──

  get envioOk(): EnvioOk | null {
    return this.resultado && esEnvioOk(this.resultado) ? this.resultado : null;
  }

  get envioError(): EnvioError | null {
    return this.resultado && !esEnvioOk(this.resultado) && !esCambioDeTasa(this.resultado)
      ? (this.resultado as EnvioError)
      : null;
  }

  /**
   * El envío quedó en estado incierto: la petición pudo haber llegado. No se
   * ofrece reintentar — se manda al historial.
   */
  get envioAmbiguo(): boolean {
    return this.envioError?.ambiguo === true;
  }

  get envioDuplicado(): boolean {
    return this.envioError?.posibleDuplicado === true;
  }

  /** Diferencia entre lo que se había confirmado y lo que se recibe ahora. */
  get deltaCambioTasa(): number | null {
    const a = this.avisoCambioTasa;
    if (!a || a.cotizacion.recibe == null) return null;
    return a.cotizacion.recibe - a.recibeAnterior;
  }

  descartarAvisoCambioTasa(): void {
    this.avisoCambioTasa = null;
  }

  /**
   * Nota al pie del resumen. En CLP→VES la promesa es otra —una baja se avisa,
   * una subida se absorbe honrando lo mostrado— y el texto lo tiene que decir,
   * o queda prometiendo algo que no cumple.
   */
  get notaTasa(): string {
    const c = this.cotizacion;
    return c && R.aplicaHonrarTasa(c.origen, c.destino)
      ? 'Verificamos la tasa antes de enviar. Si baja, te lo mostramos; si sube, respetamos el monto que ves.'
      : 'Verificamos la tasa justo antes de enviar. Si cambió, te lo mostramos.';
  }

  /**
   * Se envió honrando la tasa que el usuario vio. Es un evento de DIAGNÓSTICO,
   * no el registro de compliance: ese sale de cruzar `transacciones.tasa`,
   * `tasa_cambio_id` y `created_at` con `rates_calculated`, que es append-only
   * y fechado, así que `costo_pct` se reconstruye por consulta para cualquier
   * transacción sin escribir nada desde el front.
   *
   * Mismo formato que los eventos estructurados del backend.
   */
  private reportarTasaHonrada(referencia: string | number | null): void {
    const a = this.ajusteTasa;
    if (!a || a.costoVes === 0) return;
    console.info({
      event: 'tasa_honrada',
      corredor: `${a.origen}->${a.destino}`,
      referencia,
      monto: a.monto,
      recibeMostrado: a.recibeMostrado,
      recibeVigente: a.recibeVigente,
      costoVes: a.costoVes,
      costoPct: a.costoPct,
      tasaCambioId: a.tasaCambioId,
      cotizadoEn: a.cotizadoEn,
      confirmadoEn: a.confirmadoEn,
    });
  }

  // ───────────────────── Paso 4: comprobante ─────────────────────

  /**
   * Copia el comprobante al portapapeles.
   *
   * Copiar y no "compartir": la hoja nativa del sistema sólo existe en móvil y
   * deja al usuario sin saber qué pasó cuando no aparece. Con el texto en el
   * portapapeles, pegarlo en WhatsApp, en un mail o en un ticket es la misma
   * acción en cualquier dispositivo — y es lo que el botón promete.
   */
  async copiarComprobante(): Promise<void> {
    const c = this.comprobante;
    if (!c || this.copiando) return;

    this.copiando = true;
    this.comprobanteMsg = null;

    try {
      const texto = Comprobante.textoComprobante(c, this.locale);
      this.comprobanteMsg = (await this.copiarAlPortapapeles(texto))
        ? 'Comprobante copiado. Pegalo donde quieras.'
        : 'No pudimos copiar el comprobante, pero tu envío está confirmado: ' +
          'queda registrado en tu historial.';
    } finally {
      this.copiando = false;
    }
  }

  /**
   * Copia con dos caminos porque el primero no siempre existe: la Clipboard API
   * sólo está en contexto seguro y algunos WebViews no la exponen. Ahí entra el
   * `textarea` + `execCommand`, que es viejo pero funciona en todos.
   */
  private async copiarAlPortapapeles(texto: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto);
        return true;
      }
    } catch {
      // Sin permiso o sin contexto seguro: se intenta el camino viejo.
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, texto.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  /**
   * Historial donde el usuario puede verificar si su envío se completó. Es a
   * donde se deriva ante un resultado incierto, en vez de ofrecer un reintento
   * a ciegas que podría duplicar el envío.
   *
   * Se enruta por la MONEDA QUE SE DEBITÓ, no por el perfil del usuario:
   *
   *  · USDT → `load-pays-usdt`, que muestra el cardex USDT (`getUserCardex`).
   *    Es obligatorio: en `client/orders`, `obtenerMovimientosUsdt()` tiene un
   *    guard interno `tipoId === 'VE'`, así que un usuario CL que pagó con USDT
   *    NO vería ahí su movimiento — y podría reenviar creyendo que no pasó.
   *
   *  · CLP y VES → `client/orders`. `TransactionsComponent` bifurca por
   *    `tipoId` (VE → movimientos del cardex VES · CL → tabla `transaccion`),
   *    así que sirve a los dos perfiles. El chileno que pagó con VES va con
   *    `?moneda=VES` para ver su cardex VES y no la tabla CLP.
   */
  irAlHistorial(): void {
    if (this.navegando) return;
    this.navegando = true;
    const origen = this.cotizacion?.origen;
    if (origen === 'USDT') {
      this.router.navigate(['/wallet/client/load-pays-usdt']);
      return;
    }
    const queryParams = origen === 'VES' ? { moneda: 'VES' } : {};
    this.router.navigate(['/wallet/client/orders'], { queryParams });
  }

  /** Vuelve al home del wallet. */
  irAlInicio(): void {
    if (this.navegando) return;
    this.navegando = true;
    this.router.navigate(['/wallet/home']);
  }

  nuevoEnvio(): void {
    this.navegando = false;
    this.paso = 'cotizacion';
    this.destino = '';
    this.monto = null;
    this.montoDestino = null;
    this.direccion = 'ORIGEN';
    this.semilla = null;
    this.cotizacion = null;
    this.seleccionado = null;
    this.beneficiarios = [];
    this.busqueda = '';
    this.resultado = null;
    this.avisoCambioTasa = null;
    this.ajusteTasa = null;
    this.avisoBeneficiario = null;
    this.comprobante = null;
    this.comprobanteMsg = null;
  }

  // ───────────────────── Contexto ─────────────────────

  /**
   * Carga TODOS los saldos con los que el perfil puede pagar, no sólo el del
   * origen elegido: la decisión "¿con qué pago?" se toma viendo cuánto hay de
   * cada moneda. Se cargan una sola vez y en paralelo.
   *
   * Semántica distinta por moneda, a propósito:
   *  · CLP → `balancePermitido` = saldo + cupo de sobregiro
   *  · VES → saldo del cardex, SIN sobregiro
   *  · USDT → saldo del wallet USDT
   */
  private cargarSaldos(): void {
    const origenes = this.origenes;

    // Cupo de registro (empresa pre-aprobada). Cacheado: el `ActivateGuard` ya
    // lo trajo al navegar, así que esto casi siempre resuelve del cache. Se usa
    // para gatear el monto CLP contra el `disponible` que da el backend.
    this.cupoRegistro.ensureMe();

    if (origenes.includes('CLP') && this.saldoClp === null && this.user?.usuarioId) {
      this.cartola
        .getSaldoCliente(this.user.usuarioId)
        .then((r) => (this.saldoClp = Number(r?.balancePermitido ?? 0)))
        .catch(() => (this.saldoClp = null));
    }

    if (origenes.includes('VES') && this.saldoVes === null) {
      this.sub.add(
        this.walletVes.getMyBalance().subscribe({
          next: (r) => (this.saldoVes = Number(r?.data?.balanceVes ?? 0)),
          error: () => (this.saldoVes = null),
        }),
      );
    }

    if (origenes.includes('USDT') && this.saldoUsdt === null) {
      this.sub.add(
        this.walletVes.getMyUsdtBalance().subscribe({
          next: (r: any) => (this.saldoUsdt = Number(r?.data?.saldo ?? 0)),
          error: () => (this.saldoUsdt = null),
        }),
      );
    }
  }

  /**
   * El horario sólo condiciona el riel propio. Acá NO se redirige: se marca
   * `fiatAbierto = false` y la vista deshabilita Venezuela y Chile dejando
   * Colombia y Perú operativos.
   */
  private async verificarHorarioFiat(): Promise<void> {
    try {
      // En paralelo: encadenadas eran dos viajes de red antes de saber si
      // Venezuela y Chile están operativos.
      const [abierto, sinPausa] = await Promise.all([
        this.hoursOperation.getEstadoOperacion('FIAT'),
        this.hoursOperation.getEstadoOperacion('FIAT_STOP'),
      ]);
      this.fiatAbierto = !!abierto && !!sinPausa;
    } catch {
      // Ante error de consulta no bloqueamos: el envío revalida igual.
      this.fiatAbierto = true;
    }
  }
}
